import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unansweredQuestionsService } from '../services/unansweredQuestionsService';
import { FaqEntryInput, UnansweredQuestionStatus } from '../types';
import { faqKeys } from './useFaq';

export const unansweredQuestionKeys = {
  all: ['knowledge-base', 'unanswered-questions'] as const,
  list: (knowledgeBaseId: string, status: UnansweredQuestionStatus, search: string) =>
    ['knowledge-base', 'unanswered-questions', knowledgeBaseId, status, search] as const,
  detail: (id: string) => ['knowledge-base', 'unanswered-questions', 'detail', id] as const,
};

export const useUnansweredQuestions = (
  knowledgeBaseId: string,
  status: UnansweredQuestionStatus,
  { search = '', enabled = true }: { search?: string; enabled?: boolean } = {}
) =>
  useQuery({
    queryKey: unansweredQuestionKeys.list(knowledgeBaseId, status, search),
    queryFn: () => unansweredQuestionsService.list(knowledgeBaseId, status, search),
    placeholderData: keepPreviousData,
    enabled,
  });

// Loaded when a question is expanded: its occurrences hold transcript excerpts
export const useUnansweredQuestionDetail = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: unansweredQuestionKeys.detail(id),
    queryFn: () => unansweredQuestionsService.get(id),
    enabled,
  });

const useInvalidateUnansweredQuestions = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: unansweredQuestionKeys.all });
};

export const useAnswerUnansweredQuestion = () => {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateUnansweredQuestions();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FaqEntryInput }) =>
      unansweredQuestionsService.answer(id, data),
    onSuccess: () => {
      // The answer is a new FAQ entry
      queryClient.invalidateQueries({ queryKey: faqKeys.all });
    },
    // Also after an error: the question may have been handled meanwhile
    onSettled: invalidate,
  });
};

export const useResolveUnansweredQuestion = () => {
  const invalidate = useInvalidateUnansweredQuestions();
  return useMutation({
    mutationFn: (id: string) => unansweredQuestionsService.resolve(id),
    onSettled: invalidate,
  });
};

export const useDismissUnansweredQuestion = () => {
  const invalidate = useInvalidateUnansweredQuestions();
  return useMutation({
    mutationFn: (id: string) => unansweredQuestionsService.dismiss(id),
    onSettled: invalidate,
  });
};
